import React, { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowRightLeft, Info } from "lucide-react";
import { caToBot, dripPledge } from "@/lib/botMath";
import Stat from "@/components/calculator/Stat";

const bot = (n, dec = 2) => (isFinite(n) ? `${Number(n).toLocaleString(undefined, { maximumFractionDigits: dec })} BOT` : "—");
const usd = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");

/**
 * Where the BOT comes from in the first place: CA mining swapped across, BOT
 * hashrate mining, and the miner's own rewards — then pledged as it arrives.
 */
export default function BotSourceView({ caPerDay, caPrice, botPrice, hashBotPerDay, minerBotPerDay,
  ratePct, days, pledge, hashrateObserved }) {
  const sim = useMemo(() => {
    const swap = caToBot({ caPerDay, caPrice, botPrice });
    const perDay = swap.bot + (Number(hashBotPerDay) || 0) + (Number(minerBotPerDay) || 0);
    const rows = dripPledge({ botPerDay: perDay, ratePct, days, startingBalance: pledge });
    const last = rows[rows.length - 1] || { balance: 0, added: 0, rewards: 0 };
    return { swap, perDay, rows, last };
  }, [caPerDay, caPrice, botPrice, hashBotPerDay, minerBotPerDay, ratePct, days, pledge]);

  const D = Math.max(1, Math.round(Number(days) || 0));
  const price = Number(botPrice) || 0;
  const parts = [
    { label: "CA mining, swapped", value: sim.swap.bot, color: "#4060FF",
      note: `${Number(caPerDay) || 0} CA/day × $${Number(caPrice || 0).toFixed(2)} = ${usd(sim.swap.usd)}/day` },
    { label: "BOT hashrate mining", value: Number(hashBotPerDay) || 0, color: "#B45309",
      note: hashrateObserved?.bot_per_day != null
        ? `your wallets with bought hashrate average ${bot(hashrateObserved.bot_per_day, 4)}/day`
        : "paid in BOT against hashrate bought with USDT" },
    { label: "Miner rewards", value: Number(minerBotPerDay) || 0, color: "#7C3AED",
      note: "static + dividend — CaryPact reports these only for the wallet whose session we used, so type yours" },
  ];

  return (
    <div className="space-y-5" data-testid="bot-source-view">
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="BOT arriving per day" value={bot(sim.perDay, 4)} color="#0E7490"
          sub={`${usd(sim.perDay * price)} a day at today's BOT price`} testid="bot-source-kpi-day" />
        <Stat label={`Pledged and compounding, day ${D}`} value={bot(sim.last.balance, 2)} color="#00863A"
          sub={`${bot(sim.last.added, 2)} put in, ${bot(sim.last.rewards, 2)} earned on it`} testid="bot-source-kpi-balance" />
        <Stat label="Worth at the end" value={usd(sim.last.balance * price)} color="#7C3AED"
          sub={`at today's BOT price, before any price move`} testid="bot-source-kpi-usd" />
      </div>

      <div className="glass p-5" data-testid="bot-source-parts">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Where the BOT comes from</div>
        <ul className="space-y-2.5">
          {parts.map((p) => (
            <li key={p.label} className="flex items-start justify-between gap-4" data-testid={`bot-source-part-${p.label.split(" ")[0].toLowerCase()}`}>
              <div>
                <div className="text-sm font-semibold" style={{ color: p.color }}>{p.label}</div>
                <div className="text-[11px] text-ink-3 leading-snug">{p.note}</div>
              </div>
              <div className="font-mono text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: p.color }}>
                {bot(p.value, 4)}<span className="text-ink-3 font-normal">/day</span>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-4 mt-3 pt-3 border-t" style={{ borderColor: "rgba(15,18,38,0.08)" }}>
          <span className="text-sm font-semibold">Total arriving</span>
          <span className="font-mono text-sm font-bold tabular-nums" style={{ color: "#0E7490" }}>{bot(sim.perDay, 4)}/day</span>
        </div>
      </div>

      <div className="glass p-5" data-testid="bot-source-chart">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Pledged the day it lands</div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={sim.rows} margin={{ left: 8, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="botDrip" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0E7490" stopOpacity={0.32} /><stop offset="100%" stopColor="#0E7490" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,18,38,0.06)" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => `d${d}`} />
              <YAxis tick={{ fontSize: 11 }} width={58}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Number(v).toFixed(v < 10 ? 1 : 0))} />
              <RTooltip formatter={(v, name) => [bot(v, 4), name]} labelFormatter={(d) => `Day ${d}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="added" name="BOT put in" stroke="#94A3B8" strokeDasharray="5 4" fill="none" strokeWidth={1.5} />
              <Area type="monotone" dataKey="balance" name="Pledged + rewards" stroke="#0E7490" fill="url(#botDrip)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-ink-3 mt-3 leading-relaxed" data-testid="bot-source-note">
          Starts from the pledge on the left ({bot(Number(pledge) || 0, 2)}) and adds each day&apos;s BOT at
          {" "}{ratePct}%/day from the day it arrives. Swapping CA for BOT is a real transaction in the dApp that
          your wallet has to sign — this only shows what it would turn into.
        </p>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-source-swap">
        <ArrowRightLeft size={15} style={{ color: "#4060FF" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          The swap is priced off both tokens at once: CA ${Number(caPrice || 0).toFixed(2)} and BOT
          ${price.toFixed(2)} today, so {Number(caPerDay) || 0} CA a day buys {bot(sim.swap.bot, 4)}. If CA
          falls against BOT you get less BOT for the same mining, and the other way round.
        </p>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-source-disclaimer">
        <Info size={15} style={{ color: "#0E7490" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          Mining output, hashrate rewards and both prices all move. Treat every line as an assumption you
          control on the left, not a forecast.
        </p>
      </div>
    </div>
  );
}
